import { useCallback } from "react";

import {
  transformTagTypeConfigs,
  getTagTypesEligibleForCreate,
} from "./TagsEditor.util";
import {
  entityTagForMandatoryUniqueAttrs,
  userCreatedTagFlow,
} from "./TagsEditor.addTag";

export const useTagsEditorActions = ({
  form,
  setNewTag,
  removeTag,
  comments,
  entityId,
  tagTypesConfig,
  focusTagSelector,
  blurTagSelector,
}) => {
  const onEditTagAttributes = useCallback(
    (tag, tagTypeConfig) => {
      form.formStateDispatch({
        type: "toggleAttributesModal",
        props: { entityTag: tag, tagTypeConfig, comments },
      });
      blurTagSelector();
    },
    [blurTagSelector, comments, form]
  );

  const onCloseTagAttributes = useCallback(() => {
    form.formStateDispatch({ type: "toggleAttributesModal", props: null });
    if (form.editMode) {
      focusTagSelector();
    }
  }, [form, focusTagSelector]);

  const onCloseTagValueModal = useCallback(() => {
    form.formStateDispatch({ type: "toggleTagValueModal", props: null });
    focusTagSelector();
  }, [form, focusTagSelector]);

  const onCloseNewTagCreationModal = useCallback(() => {
    form.formStateDispatch({ type: "toggleNewTagCreationModal", props: null });
    focusTagSelector();
  }, [form, focusTagSelector]);

  const openCreateNewTagModalForUserCreatedValue = useCallback(
    (tag, tagTypeConfig) => {
      form.formStateDispatch({
        type: "toggleNewTagCreationModal",
        props: {
          entityId,
          allTags: transformTagTypeConfigs(
            getTagTypesEligibleForCreate(tagTypesConfig)
          ),
          setNewTag,
          initialDisplayValue: tag?.displayValue || "",
          initialTagType: tagTypeConfig,
          initialAttributes: tag?.attributes,
        },
      });
    },
    [entityId, tagTypesConfig, setNewTag, form]
  );

  const onAddNewTag = useCallback(
    async (tag, tagTypeConfig) => {
      if (tag.isUserCreatedTagValue) {
        const flow = userCreatedTagFlow(tagTypeConfig);
        if (flow === "create-modal") {
          openCreateNewTagModalForUserCreatedValue(tag, tagTypeConfig);
        } else if (flow === "attributes-modal") {
          form.formStateDispatch({
            type: "toggleAttributesModal",
            props: { entityTag: tag, tagTypeConfig, isNewTag: true, setNewTag },
          });
        } else {
          form.formStateDispatch({
            type: "toggleTagValueModal",
            props: { tag, tagTypeConfig, setNewTag },
          });
        }
        blurTagSelector();
        return;
      }

      const entityTagNeedingUnique = entityTagForMandatoryUniqueAttrs(
        tag,
        await setNewTag(tag, tagTypeConfig),
        tagTypeConfig
      );
      if (entityTagNeedingUnique) {
        form.formStateDispatch({
          type: "toggleAttributesModal",
          props: { entityTag: entityTagNeedingUnique, tagTypeConfig, comments },
          finishEditing: true,
        });
        return;
      }
      form.finishEditing();
    },
    [
      setNewTag,
      blurTagSelector,
      comments,
      form,
      openCreateNewTagModalForUserCreatedValue,
    ]
  );

  const onRemoveTag = useCallback(
    async (tag, tagTypeConfig) => {
      await removeTag(tag, tagTypeConfig);
    },
    [removeTag]
  );

  const onCreateNewTag = () => {
    form.formStateDispatch({
      type: "toggleNewTagCreationModal",
      props: {
        allTags: transformTagTypeConfigs(
          getTagTypesEligibleForCreate(tagTypesConfig)
        ),
        setNewTag,
        entityId,
      },
    });
  };

  return {
    onEditTagAttributes,
    onCloseTagAttributes,
    onCloseTagValueModal,
    onCloseNewTagCreationModal,
    onAddNewTag,
    onRemoveTag,
    onCreateNewTag,
  };
};
